import styled from 'styled-components';
import { cardShadow } from '../../themes/shadows';

export const ClosedContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 50%;
  background: var(--cco-red);
  position: relative;
  width: 100px;
  height: 110px;
  top: -50px;
  right: -50px;
  position: absolute;
  cursor: pointer;
  z-index: 2;
  /* Reset de styles padroes de button (renderizado como <button> com
     prop "as" pra teclado/screen-reader funcionarem corretamente) */
  border: none;
  padding: 0;
  font-family: inherit;

  :focus-visible {
    outline: 3px solid var(--cco-ink);
    outline-offset: 2px;
  }

  div {
    position: absolute;
    text-align: center;
    left: 25px;
    bottom: 22px;
    display: flex;
    flex-direction: column;
    color: #fff;
    font-size: 14px;
    margin-top: auto;
    > img {
      width: 18px;
    }
  }
`;

export const OpenContainer = styled.div`
  /* Surface CCO no lugar do overlay vermelho macico (ilegivel e fora do
     design). A semantica de alarme fica nos badges/accents, nao no fundo. */
  background: var(--cco-bg);
  border-top: 3px solid var(--cco-red);
  z-index: 3;
  display: flex;
  flex-direction: column;
  position: absolute;
  width: calc(100vw - 120px);
  height: calc(100vh - 105px);
  padding: 45px 40px 60px 80px;
  box-sizing: border-box;

  /* Mobile: o overlay ocupa a tela toda (sem o offset de 80px que era
     pro sidebar) e reduz o padding pra caber. */
  @media (max-width: 720px) {
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    padding: 16px 14px 70px;
  }

  > * {
    animation: fadeIn ease-in 1s;
    -webkit-animation: fadeIn ease-in 1s;
    -moz-animation: fadeIn ease-in 1s;
    -o-animation: fadeIn ease-in 1s;
    -ms-animation: fadeIn ease-in 1s;
  }
`;

export const Line = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: ${(props) => props.flex};
  color: var(--cco-ink);
`;

// IconWrapper renderizado como button via "as" prop nos pontos clicaveis
// (close, delete, etc) para suportar foco por teclado e screen readers.
// Reset de botao quando renderiza como button.
export const IconWrapper = styled.div`
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${(props) =>
    props.color ? props.color : 'var(--cco-soft-surface)'};
  border: 1px solid var(--cco-line);
  width: 27px;
  height: 27px;
  line-height: 22px;
  font-size: 17px;
  margin-right: 5px;
  ${(props) => !props.unclickable && `cursor: pointer;`}
  color: var(--cco-red);
  padding: 0;
  font-family: inherit;

  img {
    width: 17px;
    cursor: inherit;
  }
  :hover {
    ${(props) =>
      !props.unclickable &&
      `transition: transform 270ms; transform: scale(1.1);`}
  }
  :focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.lightblue};
    outline-offset: 2px;
  }
`;

// Tag renderizada como button via "as" prop nos filtros (todos/alarmes/etc).
// aria-pressed indica selecao para screen readers; estado :focus-visible
// destaca por teclado.
export const Tag = styled.div`
  text-align: center;
  padding: 5px 15px;
  min-width: 90px;
  font-weight: 800;
  font-size: 15px;
  border-radius: 999px;
  background: var(--cco-soft-surface);
  transition: all 270ms;
  cursor: pointer;
  color: var(--cco-ink-mute);
  border: 1px solid var(--cco-line);
  font-family: inherit;

  &[aria-pressed='true'] {
    background: var(--cco-red);
    color: #fff;
    border-color: transparent;
  }

  ${(props) =>
    !props.disabled &&
    `
    :hover {
      border-color: var(--cco-line-strong);
      color: var(--cco-ink);
    }
  `}
  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed;
    opacity: 0.7;
  `}

  :focus-visible {
    outline: 2px solid var(--cco-blue);
    outline-offset: 2px;
  }
`;

export const Column = styled.div`
  flex: ${(props) => props.flex};
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
`;

export const Evento = styled.div`
  padding: 10px 20px;
  width: calc(100% - 40px);
  margin: 4px 0;
  border-radius: 10px;
  background: var(--cco-surface);
  border: 1px solid var(--cco-line);
  display: grid;
  place-items: center;
  grid-template-columns: 1fr 2fr 2fr 2fr 5fr 2fr 2fr 1fr;
  box-shadow: ${cardShadow};

  h3 {
    color: var(--cco-ink);
    font-size: var(--fs-title);
  }
  h4 {
    color: var(--cco-ink-faint);
    font-size: var(--fs-body);
  }
  label {
    font-weight: 700;
    font-size: var(--fs-body);
    color: var(--cco-ink-mute);
  }

  /* Mobile: a linha de 8 colunas nao cabe — vira card empilhado
     (cada celula em sua propria linha, alinhada a esquerda). */
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    place-items: stretch;
    gap: 4px;
    text-align: left;
    width: 100%;
    padding: 10px 14px;
  }
`;

// Badge colorido por tipo de evento — alarme (amarelo, atencao), bloqueio
// (vermelho, falha operacional), trip (vermelho escuro, protecao acionou).
// Operador identifica severidade de relance sem precisar ler a label.
export const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #fff;
  background: ${(props) => {
    const t = (props.tipo || '').toLowerCase();
    if (t === 'alarme') return 'var(--cco-orange)';
    if (t === 'trip') return '#7A1818';
    return 'var(--cco-red)';
  }};
`;

export const EventButton = styled.button`
  padding: 5px 15px;
  border-radius: 10px;
  cursor: pointer;
  color: #fff;
  transition: all 270ms;
  border: 1px solid
    ${(props) => (props.isRed ? 'var(--cco-red)' : 'var(--cco-green)')};
  background: ${(props) =>
    props.isRed ? 'var(--cco-red)' : 'var(--cco-green)'};
  :hover {
    color: ${(props) => (props.isRed ? 'var(--cco-red)' : 'var(--cco-green)')};
    background: transparent;
  }
`;

export const EmptyLabel = styled.label`
  color: var(--cco-ink-mute);
  font-style: italic;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
`;
